<script lang="ts" setup>
import { ParagraphFormatting } from "types/Paragraph";

// @ts-ignore
import DOMPurify from "dompurify";
import { SelectionType } from "~/classes/models/self-assessment/self-assessment-step.model";

const props = defineProps({
  baseToken: {
    type: Object as PropType<ParagraphToken>,
    required: true,
  },
  paragraphFormatting: {
    type: Object as PropType<ParagraphFormatting>,
    required: true,
  },
  isAssignmentToken: {
    type: Boolean,
    required: false,
    default: false,
  },
  enableComments: {
    type: Boolean,
    required: false,
    default: true,
  },
});

const documentId = inject("documentId") as any;

const documentViewModeStore = useDocumentViewModeStore(documentId);
const { isTeacherViewMode } = storeToRefs(documentViewModeStore);

const tokenStore = useParagraphTokenStore(
  documentId,
  props.baseToken.globalPosition
);
const { token: storedToken } = storeToRefs(tokenStore);

const token = computed(() => {
  return storedToken.value ?? props.baseToken;
});

const appUserDataStore = useAppUserDataStore();
const { commentColor } = storeToRefs(appUserDataStore);

const lineHeight = computed(() => {
  return props.paragraphFormatting.lineSpacing;
});

const documentSelectionStore = useDocumentSelectionStore(documentId);
const { evaluativeParagraphIndexToHighlight, documentSelectionCallback } =
  storeToRefs(documentSelectionStore);

const documentTokenSelectorStore = useDocumentTokenSelectorStore(documentId);
const { documentSelectionType } = storeToRefs(documentTokenSelectorStore);

const isTokenSelected = ref(
  documentSelectionStore.isTokenSelected(token.value.globalPosition)
);

onUpdated(() => {
  isTokenSelected.value = documentSelectionStore.isTokenSelected(
    token.value.globalPosition
  );
});

const highlightColor = computed(() => {
  if (paragraphHighlight.value != undefined) {
    return undefined;
  }

  return useTokenHighlightColor(token.value?.formatting?.highlightColor);
});

const { generateTokenElementId } = useTokenElementIdGenerator();

// Event Handlers
const isSelectable = ref(false);
const showManageStylePopover = ref(false);
const isTokenHighlighted = ref(false);

const onMouseDown = (ev: MouseEvent) => {
  isSelectable.value =
    documentSelectionStore.selectedTokenPositions.length <= 1;

  if (ev.ctrlKey || ev.metaKey) {
    ev.stopPropagation();
    ev.preventDefault();
    return;
  }
};

const documentActiveTypeStore = useDocumentActiveTypeStore(documentId);
const { activeTypes } = storeToRefs(documentActiveTypeStore);
const documentHighlightStore = useDocumentHighlightStore(documentId);

const onContextMenu = (ev: MouseEvent) => {
  if (ev.button == 0) {
    documentSelectionStore.toggleCustomPosition(token.value.globalPosition);
    ev.preventDefault();
    return;
  }
};

const onClick = (ev: MouseEvent) => {
  isTokenHighlighted.value = false;

  if (documentTokenSelectorStore.documentSelectionCallback != undefined) {
    documentTokenSelectorStore.documentSelectionCallback(
      token.value.globalPosition
    );
    return;
  }

  if (
    documentActiveTypeStore.hasActiveTypes == true &&
    token.value.formatting?.underline
  ) {
    showManageStylePopover.value = true;
    isTokenHighlighted.value = documentHighlightStore.isTokenHighlighted(
      token.value.globalPosition
    );
  }

  if (ev.ctrlKey || ev.metaKey) {
    documentSelectionStore.toggleCustomPosition(token.value.globalPosition);

    ev.stopPropagation();
    ev.preventDefault();
    return;
  }

  if (isSelectable.value != true) {
    isSelectable.value = false;
    return;
  }

  const selection = window.getSelection();
  const targetNode = ev.target as Node;

  if (selection && targetNode && targetNode.firstChild) {
    const range = document.createRange();
    range.selectNodeContents(targetNode.firstChild!);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  isSelectable.value = false;
};

const onDoubleClick = async (ev: any) => {
  if (props.enableComments != true) return;

  if (isTeacherViewMode.value != true) return;

  documentSelectionStore.setSelection(
    token.value.globalPosition,
    token.value.globalPosition
  );

  useCreateInlineEditingAnnotation(documentId);
};

const draggingAnnotationStore = useDraggingAnnotation();
const { isAnAnnotationBeingDragged } = storeToRefs(draggingAnnotationStore);

const onMouseEnter = () => {
  // We only want to highlight sentences if the subtype is set.

  if (documentSelectionType.value != undefined) {
    switch (documentSelectionType.value) {
      case SelectionType.token:
        documentTokenSelectorStore.mouseEnterTokens([
          token.value.globalPosition,
        ]);
        break;
      case SelectionType.sentence:
        const sentenceTokenPositions = useTokenGlobalPositionToSentenceTokens(
          documentId,
          token.value.globalPosition
        );

        documentTokenSelectorStore.mouseEnterTokens(sentenceTokenPositions);
        break;
      case SelectionType.paragraph:
        const paragraphTokenPositions =
          useTokenGlobalPositionsToParagraphTokens(
            documentId,
            token.value.globalPosition
          );

        documentTokenSelectorStore.mouseEnterTokens(paragraphTokenPositions);
        break;
    }

    return;
  }

  if (
    isTeacherViewMode.value != true &&
    activeTypes.value.includes("sentenceOpener")
  ) {
    const sentence = useDocumentEvaluativeParagraphsStore(
      documentId
    ).sentenceFromTokenId(token.value.id);

    if (sentence == undefined) return;

    const sentenceTokenPositions = sentence.tokens.map((t) =>
      useDocumentTokenIdPositionMapStore(documentId).tokenGlobalPositionFromId(
        t.id
      )
    );

    useApplyClassToTokens(
      sentenceTokenPositions,
      "token-is-sentence-highlighted"
    );
  }

  if (draggingAnnotationStore.isAnAnnotationBeingDragged != true) {
    return;
  }

  draggingAnnotationStore.setSelectedTokenGlobalPosition(
    token.value.globalPosition
  );
};

const onMouseLeave = async () => {
  if (documentSelectionType.value != undefined) {
    switch (documentSelectionType.value) {
      case SelectionType.token:
        documentTokenSelectorStore.mouseLeaveTokens([
          token.value.globalPosition,
        ]);
        break;
      case SelectionType.sentence:
        const sentenceTokenPositions = useTokenGlobalPositionToSentenceTokens(
          documentId,
          token.value.globalPosition
        );
        documentTokenSelectorStore.mouseLeaveTokens(sentenceTokenPositions);
        break;
      case SelectionType.paragraph:
        const paragraphTokenPositions =
          useTokenGlobalPositionsToParagraphTokens(
            documentId,
            token.value.globalPosition
          );
        documentTokenSelectorStore.mouseLeaveTokens(paragraphTokenPositions);
        break;
    }

    return;
  }

  useApplyClassToTokens([], "token-is-sentence-highlighted");

  if (draggingAnnotationStore.isAnAnnotationBeingDragged != true) {
    return;
  }

  draggingAnnotationStore.removeSelectedTokenGlobalPosition(
    token.value.globalPosition
  );
};

const documentShowFormattingStore = useDocumentShowFormattingStore(documentId);
const { shouldShowFormatting } = storeToRefs(documentShowFormattingStore);

// right I think the problem is that mabne html instead?
const tokenContent = computed(() => {
  if (token.value.type == "page_number") {
    return "1";
  }

  if (shouldShowFormatting.value != true) {
    if (token.value?.type == "space" || token.value?.type == "filler") {
      if (/  +/.test(token.value?.text)) {
        return token.value?.text
          .replace(/ /g, " &nbsp;")
          .replace(/\t/g, "&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;")
          .replace(/\n/g, "");
      } else {
        return token.value?.text
          .replace(/ /g, " ")
          .replace(/\t/g, "&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;")
          .replace(/\n/g, "");
      }
    }

    return token.value?.text
      .replace(/\t/g, "&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;")
      .replace(/\n/g, "");
  }

  if (token.value?.type == "filler" || token.value?.type == "space")
    return token.value?.text
      .replace(/ /g, " <span class='text-danger-default text-[10px]'>•</span> ")
      .replace(
        /\t/g,
        "<span class='text-danger-default text-[10px] mr-1'>➔</span>"
      );

  if (token.value?.type == "page_break")
    return "<span class='text-danger-default block mx-auto text-center w-full mt-2 mb-4'>--- Page Break ---</span>";

  return token.value?.text.replace(
    /\t/g,
    "<span class='text-danger-default text-[10px]'>→</span>"
  );
});

const tokenRef = ref();

onMounted(() => {
  setTimeout(() => {
    if (token.value.text == "\n") {
      return;
    }

    if (tokenRef.value == undefined) return;

    // Ok so far so good. Can I get the x and y coordinate sof this?
    const tokenElement = tokenRef.value as HTMLElement | undefined;

    if (tokenElement == undefined) return;

    const tokenRect = tokenElement.getBoundingClientRect();

    useDocumentTraversalStore(documentId).mapTokenCoordinates(
      token.value.globalPosition,
      tokenRect.x,
      tokenRect.y
    );
  }, 500);
});

const textDecorationLineValue = computed(() => {
  let value = "";

  if (token.value.formatting?.underline) {
    value += "underline ";
  }

  if (token.value.formatting?.strikeThrough) {
    value += "line-through ";
  }

  return value;
});

const documentZoomControlStore = useDocumentZoomControllStore(documentId);
const { zoomFactor } = storeToRefs(documentZoomControlStore);

const fontSize = computed(() => {
  var size = token.value?.formatting?.size;

  if (size == undefined) {
    size = 12;
  }

  return size * zoomFactor.value + "pt";
});

const textColor = computed(() => {
  return useArrayToRGB(token.value?.formatting?.color ?? []);
});

const documentEvaluativeParagraphStore =
  useDocumentEvaluativeParagraphsStore(documentId);
const { getEvaluativeParagraphByTokenIndex } = storeToRefs(
  documentEvaluativeParagraphStore
);

const paragraphHighlight = computed(() => {
  if (props.isAssignmentToken != true) return undefined;

  if (shouldShowFormatting.value != true) return undefined;

  const evaluativeParagraph = getEvaluativeParagraphByTokenIndex.value(
    token.value.position
  );

  if (evaluativeParagraph == undefined) return undefined;

  if (evaluativeParagraph.section != "body") return undefined;

  // If evaluativeParagraph.index is even then color it red otherwise blue
  if (evaluativeParagraph.index % 2 == 0) {
    return {
      [`bg-primary-default ${
        evaluativeParagraphIndexToHighlight.value == evaluativeParagraph.index
          ? "bg-opacity-20"
          : " bg-opacity-5"
      }`]: true,
    };
  } else {
    return {
      [`bg-secondary-default ${
        evaluativeParagraphIndexToHighlight.value == evaluativeParagraph.index
          ? "bg-opacity-20"
          : "bg-opacity-5"
      }`]: true,
    };
  }
});

const fontFamily = computed(() => {
  let tokenFont = token.value?.formatting?.font;

  let font = "Times New Roman";

  if (tokenFont != undefined && tokenFont?.includes("Times") != true) {
    font = "Lato";
  }

  return `${font} !important`;
});
const imageWidth = computed(() => {
  var magnitude = token.value.imageData?.width?.magnitude;
  var unit = token.value.imageData?.width?.unit;

  if (magnitude == undefined || unit == undefined) return undefined;

  return `${magnitude}${unit}`;
});

const imageHeight = computed(() => {
  var magnitude = token.value.imageData?.height?.magnitude;
  var unit = token.value.imageData?.height?.unit;

  if (magnitude == undefined || unit == undefined) return undefined;

  return `${magnitude}${unit}`;
});
</script>

<template>
  <span
    v-if="token.type != 'image'"
    ref="tokenRef"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @mousedown="onMouseDown"
    @contextmenu="onContextMenu"
    @click="onClick"
    @dblclick="onDoubleClick"
    :id="generateTokenElementId(token.globalPosition)"
    :data-step-id="token.id"
    :data-token-id="token.id"
    :data-token-local-position="token.position"
    :data-token-position="token.globalPosition"
    :style="{
      'scroll-margin-top': '50px',
      'scroll-margin-bottom': '50px',
      'font-family': fontFamily,
      'font-size': fontSize,
      color: textColor,
      'text-decoration-color':
        isTeacherViewMode == true ? useRGBToHex(commentColor) : undefined,

      'line-height': lineHeight,
      'background-color': highlightColor,
      'text-decoration-line': textDecorationLineValue,
    }"
    class="paragraph-token token-text decoration-2"
    :class="{
      'select-none': enableComments != true,
      'font-[600]': token?.formatting?.bold,
      italic: token?.formatting?.italic,
      ...paragraphHighlight,
    }"
  >
    <span
      class="inner-token"
      :class="{
        'selection:bg-[#DFECFF] selection:bg-opacity-80':
          isAnAnnotationBeingDragged != true &&
          (isTeacherViewMode == true ||
            activeTypes.includes('sentenceOpener') != true),
        'selection:bg-none': isAnAnnotationBeingDragged == true,
      }"
    >
      <span class="highlight-layer">
        <span
          class="selection-layer"
          v-html="DOMPurify.sanitize(tokenContent)"
        ></span>
      </span>
    </span>
  </span>
  <span
    v-if="token.type == 'image'"
    :style="{ width: imageWidth, height: imageHeight }"
    class="bg-light-default block p-2"
  >
    <span>
      <p class="mb-2">
        <BaseIconImageOff class="mb-1" /> Images pasted or inserted in documents
        cannot be displayed properly.
      </p>
      <p>
        If you need to upload multiple images, hold Ctrl (Cmd on a Mac) and
        click each image you want to upload. Your selected images will be
        combined in IEW Gradebook as a single submission.
      </p>
    </span>
  </span>
</template>

<style scoped>
.token-selected .selection-layer {
  @apply bg-primary-default;
  @apply bg-opacity-20;
}

.should-highlight-token,
.token-is-dragging-selected {
  @apply bg-yellow-200;
}

.token-is-highlighted {
  @apply bg-highlight-default;
  @apply bg-opacity-70;
}

.token-is-sentence-highlighted {
  @apply bg-highlight-default;
  @apply bg-opacity-70;
}

.token-is-highlight-selected {
  @apply bg-highlight-selected;
  @apply bg-opacity-60;
}

.custom-selection {
  @apply bg-[#DFECFF];
  @apply bg-opacity-80;
}
</style>
